import {
  PendingAction,
  AuditEntry,
  AuditFilters,
  StatsSummary,
  ActionStatus,
} from '../types';

// Resolve API base through the shared module (absolute by default so
// the UI works when served from Static Web Apps as well as the Function host).
import { API_BASE, apiFetch } from './base';

// Initial Mock Data Store for resilient offline/fallback experience
const mockActionsStore: PendingAction[] = [
  {
    actionId: 'act_mkt_9021',
    agentName: 'Campaign Optimizer Agent',
    workstream: 'marketing',
    actionType: 'Ad Budget Allocation & Ad Set Launch',
    description: 'Reallocate $12,500 monthly search ad spend from low-converting keyword sets to high-intent "Urgent Care Near Me" local ad sets across 4 PortCo clinics.',
    targetEntity: {
      id: 'mkt_camp_442',
      type: 'Google Ads Campaign',
      name: 'Q3 Regional Patient Acquisition - Metro West',
      details: {
        currentBudget: '$35,000/mo',
        proposedBudget: '$47,500/mo',
        targetCPA: '$42.50',
        expectedLeadLift: '+28%'
      }
    },
    riskLevel: 'medium',
    estimatedImpact: '+$48,000 net new patient billings / month (Est. 120 new patient visits @ $400 avg LTV)',
    rationale: 'Cost-per-acquisition for generic keywords ballooned to $112. Local intent keywords maintain a $38 CPA with 18% conversion to booked appointment.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_apex',
    historicalContext: {
      pastActionsCount: 14,
      successRate: '92.8%',
      similarActionSummary: 'Last month reallocated $8k with +31% conversion improvement in South Region.',
      lastExecutedAt: '2026-07-28T14:30:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
        event: 'Action Created',
        actor: 'Campaign Optimizer Agent (v2.4)',
        details: 'Evaluated 30-day conversion telemetry and generated reallocation proposal'
      },
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
        event: 'Teams Notification Sent',
        actor: 'Orchestrator Agent',
        details: 'Dispatched Adaptive Card review request to #mkt-approvals'
      }
    ]
  },
  {
    actionId: 'act_ref_8044',
    agentName: 'Physician Liaison Agent',
    workstream: 'referrals',
    actionType: 'Outbound Referral Partner Outreach & Schedule Tiering',
    description: 'Send automated customized referral partner outreach to 18 orthopedists for priority intake workflow & direct EHR scheduling integration.',
    targetEntity: {
      id: 'ref_group_901',
      type: 'Provider Network',
      name: 'Tri-County Orthopedic Specialists (18 MDs)',
      details: {
        specialty: 'Orthopedics & Sports Medicine',
        historicalReferrals: '14/mo',
        targetReferrals: '45/mo'
      }
    },
    riskLevel: 'low',
    estimatedImpact: '+31 referral bookings / month (~$62,000 clinical revenue)',
    rationale: 'Tri-County Orthopedics expressed interest in direct EHR queueing. Drafted HIPAA-compliant onboarding kit and dedicated provider scheduler link.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_summit',
    historicalContext: {
      pastActionsCount: 22,
      successRate: '95.5%',
      similarActionSummary: 'Enrolled 5 provider groups in Q2 with zero delivery or compliance flags.',
      lastExecutedAt: '2026-08-02T09:15:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        event: 'Action Created',
        actor: 'Physician Liaison Agent (v1.9)',
        details: 'Drafted personalized email sequence & provider scheduling link'
      }
    ]
  },
  {
    actionId: 'act_int_3319',
    agentName: 'Patient Intake Agent',
    workstream: 'intake',
    actionType: 'Prior Authorization Submission',
    description: 'Submit urgent prior-authorization request for MRI Lumbar Spine with BlueCross BlueShield including attached clinical notes and peer review justification.',
    targetEntity: {
      id: 'pat_982110',
      type: 'Patient Record & Auth Request',
      name: 'Patient #982110 (John Doe - Lumbar MRI)',
      details: {
        payer: 'BCBS Michigan',
        cptCode: '72148',
        icd10: 'M54.50 (Low back pain)',
        orderingProvider: 'Dr. Emily Vance, MD'
      }
    },
    riskLevel: 'medium',
    estimatedImpact: 'Prevents $3,400 claim denial risk & speeds up patient procedure scheduling by 4 days.',
    rationale: 'Automated clinical documentation scraper gathered 6 weeks of failed conservative therapy notes required by BCBS policy #PA-402.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 65).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_apex',
    historicalContext: {
      pastActionsCount: 88,
      successRate: '97.7%',
      similarActionSummary: '88 prior auth submissions processed with 98% first-pass approval rate.',
      lastExecutedAt: '2026-08-11T16:20:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 65).toISOString(),
        event: 'Action Created',
        actor: 'Patient Intake Agent (v3.1)',
        details: 'Compiled medical record history and validated policy authorization rule engine'
      }
    ]
  },
  {
    actionId: 'act_sch_1102',
    agentName: 'Capacity & Scheduling Agent',
    workstream: 'scheduling',
    actionType: 'Overbook & Schedule Slot Rebalancing',
    description: 'Adjust Friday morning schedule templates to auto-fill 12 predicted high-no-show MRI slots with waitlisted acute pain patients.',
    targetEntity: {
      id: 'sch_tmpl_883',
      type: 'Schedule Template',
      name: 'Advanced Imaging Center - MRI Bay 2',
      details: {
        location: 'West Campus Diagnostic Center',
        predictedNoShowRate: '24%',
        fillRateTarget: '95%'
      }
    },
    riskLevel: 'medium',
    estimatedImpact: 'Recovers $7,200 in wasted scanner idle time; reduces waitlist time by 2.3 days.',
    rationale: 'ML no-show model flagged 12 Friday appointments with >60% no-show probability based on weather, patient history, and transportation status.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_radiant',
    historicalContext: {
      pastActionsCount: 45,
      successRate: '91.1%',
      similarActionSummary: 'Schedule rebalancing at North Bay added 18 completed scans without wait time breaches.',
      lastExecutedAt: '2026-08-08T11:00:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
        event: 'Action Created',
        actor: 'Capacity & Scheduling Agent',
        details: 'Evaluated predictive schedule density model for Friday AM slots'
      }
    ]
  },
  {
    actionId: 'act_rcm_5590',
    agentName: 'RCM Claims Resolution Agent',
    workstream: 'rcm',
    actionType: 'Batch Claim Appeal & Denial Overturn',
    description: 'Submit batch appeal with attached proof of timely filing and updated modifier 25 for 42 denied claims totaling $148,500 across UnitedHealthcare.',
    targetEntity: {
      id: 'batch_appeal_204',
      type: 'Denial Claim Batch',
      name: 'UHC Outpatient Surgical Claim Denial Batch #204',
      details: {
        payer: 'UnitedHealthcare Commercial',
        claimCount: 42,
        totalValue: '$148,500.00',
        denialReasonCode: 'CO-97 (Inclusive procedure code)'
      }
    },
    riskLevel: 'high',
    estimatedImpact: 'Recovers $118,800 estimated net collections (80% recovery target); protects aging collections balance >90 days.',
    rationale: 'Claims were wrongly denied under improper bundling edits. Medical records prove separate procedural site & independent evaluation.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 140).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_vanguard',
    historicalContext: {
      pastActionsCount: 31,
      successRate: '87.1%',
      similarActionSummary: 'UHC denial batch in June yielded $94k recovery within 21 days.',
      lastExecutedAt: '2026-07-15T13:45:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 140).toISOString(),
        event: 'Action Created',
        actor: 'RCM Claims Resolution Agent',
        details: 'Aggregated 42 claims, generated CMS-1500 appeal packages with clinical documentation'
      }
    ]
  },
  {
    actionId: 'act_hr_7712',
    agentName: 'Credentialing & HR Agent',
    workstream: 'hr',
    actionType: 'Locum Tenens Emergency Onboarding & License Sync',
    description: 'Execute expedited credentialing verifications and state board license sync for 3 incoming Locum Tenens Anesthesiologists for Q3 coverage.',
    targetEntity: {
      id: 'hr_cred_909',
      type: 'Provider Onboarding Pack',
      name: 'Locum Anesthesiology Group (Drs. Smith, Miller, Patel)',
      details: {
        facilities: 'St. Jude Surgical Center & Main Hospital',
        startDate: '2026-08-20',
        verificationStatus: 'NPI & NPDB Checked, State Board Verified'
      }
    },
    riskLevel: 'high',
    estimatedImpact: 'Prevents 14 surgical suite cancellations ($180,000 revenue) due to staffing shortages.',
    rationale: 'Primary verification checks via NIMS and NPDB cleared. Hospital privilege committees require human ops signoff prior to final system access provisioning.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_apex',
    historicalContext: {
      pastActionsCount: 19,
      successRate: '100.0%',
      similarActionSummary: '19 locum onboarding workflows completed with zero compliance exceptions.',
      lastExecutedAt: '2026-08-01T10:00:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
        event: 'Action Created',
        actor: 'Credentialing & HR Agent',
        details: 'Fetched NPDB query responses and validated malpractice coverage certificates'
      }
    ]
  },
  {
    actionId: 'act_fin_0041',
    agentName: 'Capital & Treasury Agent',
    workstream: 'finance',
    actionType: 'Automated Vendor Disbursement & Invoice Settlement',
    description: 'Approve $214,800 batch wire disbursement to MedSupply Direct for discounted bulk surgical consumable purchase order #PO-9912.',
    targetEntity: {
      id: 'fin_po_9912',
      type: 'Vendor Invoice & Disbursement',
      name: 'MedSupply Direct PO #PO-9912',
      details: {
        vendor: 'MedSupply Direct Inc',
        invoiceAmount: '$214,800.00',
        earlyPayDiscount: '2.5% ($5,370 savings if paid today)',
        glAccount: '5100 - Medical Supplies Expense'
      }
    },
    riskLevel: 'critical',
    estimatedImpact: 'Captures $5,370 early-payment cash discount; avoids $1,200 late penalty fee.',
    rationale: '3-way match verified (PO, Receiving Slip, Invoice match 100%). Cash balance across treasury account is $4.2M, well above minimum liquid reserve thresholds.',
    proposedAt: new Date(Date.now() - 1000 * 60 * 210).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_apex',
    historicalContext: {
      pastActionsCount: 52,
      successRate: '98.1%',
      similarActionSummary: '52 wire disbursements over $100k executed. 1 manual correction on GL coding.',
      lastExecutedAt: '2026-08-05T15:10:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 210).toISOString(),
        event: 'Action Created',
        actor: 'Capital & Treasury Agent',
        details: 'Executed 3-way match audit and generated wire payload for ACH/FedWire queue'
      }
    ]
  },
  {
    actionId: 'act_clin_6118',
    agentName: 'Clinical Protocol Agent',
    workstream: 'clinical-ops',
    actionType: 'High-Risk Lab Alert Escalation Protocol',
    description: 'Trigger immediate provider call alert & emergency outreach for Critical Lab Value (Troponin I = 4.8 ng/mL) for Patient #44102.',
    targetEntity: {
      id: 'lab_crit_44102',
      type: 'Lab Result Flag',
      name: 'Patient #44102 Critical Lab Result',
      details: {
        test: 'Troponin I High-Sensitivity',
        resultValue: '4.8 ng/mL (Ref <0.04)',
        orderingClinician: 'Dr. Marcus Thorne',
        patientLocation: 'Outpatient Clinic #4 - Exam 3'
      }
    },
    riskLevel: 'critical',
    estimatedImpact: 'Ensures urgent cardiac transfer (<15 min response time); mitigates life-critical outcome & clinical risk.',
    rationale: 'Automated panic-lab watcher detected critical elevation. Multi-channel escalation triggered (EHR alert, phone dispatch, clinical manager notification).',
    proposedAt: new Date(Date.now() - 1000 * 60 * 250).toISOString(),
    status: 'pending_review',
    portcoId: 'portco_summit',
    historicalContext: {
      pastActionsCount: 64,
      successRate: '100.0%',
      similarActionSummary: '64 critical lab alerts escalated. Mean response time 4.2 minutes.',
      lastExecutedAt: '2026-08-10T18:22:00Z'
    },
    auditTrail: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 250).toISOString(),
        event: 'Action Created',
        actor: 'Clinical Protocol Agent',
        details: 'Lab watcher engine flagged critical panic threshold (>2.0 ng/mL)'
      }
    ]
  }
];

const mockAuditLogStore: AuditEntry[] = [
  {
    id: 'aud_1001',
    actionId: 'act_rcm_5101',
    timestamp: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
    agentName: 'RCM Claims Resolution Agent',
    workstream: 'rcm',
    actionType: 'Claim Re-submission with Modifier 59',
    description: 'Re-submitted 18 rejected outpatient surgery claims to Aetna.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 1420,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1002',
    actionId: 'act_mkt_8911',
    timestamp: new Date(Date.now() - 1000 * 60 * 480).toISOString(),
    agentName: 'Campaign Optimizer Agent',
    workstream: 'marketing',
    actionType: 'Social Media Ad Campaign Launch',
    description: 'Launch Instagram ad campaign targeting senior care decision makers.',
    status: 'rejected',
    reviewer: 'Mark Reynolds',
    rejectionReason: 'Creative copy needs compliance review regarding Medicare advantage terminology.',
    processingTimeMs: 2850,
    portcoName: 'Summit Behavioral Health'
  },
  {
    id: 'aud_1003',
    actionId: 'act_sch_0921',
    timestamp: new Date(Date.now() - 1000 * 60 * 600).toISOString(),
    agentName: 'Capacity & Scheduling Agent',
    workstream: 'scheduling',
    actionType: 'Provider Shift Swap & Overtime Authorization',
    description: 'Authorized 8 hours overtime for weekend nurse coverage at Urgent Care West.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 980,
    portcoName: 'Radiant Dental Group'
  },
  {
    id: 'aud_1004',
    actionId: 'act_fin_9912',
    timestamp: new Date(Date.now() - 1000 * 60 * 800).toISOString(),
    agentName: 'Capital & Treasury Agent',
    workstream: 'finance',
    actionType: 'Vendor Payment Disbursement',
    description: 'Disbursed $45,200 to Siemens Healthineers for MRI maintenance contract.',
    status: 'approved',
    reviewer: 'Elena Rostova',
    processingTimeMs: 1150,
    portcoName: 'Vanguard Urgent Care'
  },
  {
    id: 'aud_1005',
    actionId: 'act_int_2201',
    timestamp: new Date(Date.now() - 1000 * 60 * 1100).toISOString(),
    agentName: 'Patient Intake Agent',
    workstream: 'intake',
    actionType: 'Insurance Verification Batch',
    description: 'Processed real-time eligibility check for 142 scheduled patient visits tomorrow.',
    status: 'approved',
    reviewer: 'System Direct-Approve (Low Risk)',
    processingTimeMs: 640,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1006',
    actionId: 'act_rcm_5592',
    timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    agentName: 'RCM Claims Resolution Agent',
    workstream: 'rcm',
    actionType: 'Denial Code Analysis & Appeal Strategy',
    description: 'Analyzed 42 UHC denials (CO-97) and generated targeted appeal packages with modifier 25 documentation.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 2100,
    portcoName: 'Vanguard Urgent Care'
  },
  {
    id: 'aud_1007',
    actionId: 'act_mkt_9025',
    timestamp: new Date(Date.now() - 1000 * 60 * 240).toISOString(),
    agentName: 'Campaign Optimizer Agent',
    workstream: 'marketing',
    actionType: 'Google Ads Budget Reallocation',
    description: 'Reallocated $8,000 from generic keywords to "urgent care near me" local intent ad sets.',
    status: 'approved',
    reviewer: 'Mark Reynolds',
    processingTimeMs: 850,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1008',
    actionId: 'act_ref_8050',
    timestamp: new Date(Date.now() - 1000 * 60 * 300).toISOString(),
    agentName: 'Physician Liaison Agent',
    workstream: 'referrals',
    actionType: 'Referral Partner Onboarding',
    description: 'Onboarded Tri-County Orthopedics (18 MDs) with direct EHR scheduling integration.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 1650,
    portcoName: 'Summit Behavioral Health'
  },
  {
    id: 'aud_1009',
    actionId: 'act_int_3325',
    timestamp: new Date(Date.now() - 1000 * 60 * 340).toISOString(),
    agentName: 'Patient Intake Agent',
    workstream: 'intake',
    actionType: 'Prior Authorization Auto-Submission',
    description: 'Submitted prior auth for MRI Lumbar Spine (CPT 72148) to BCBS with 6 weeks of conservative therapy documentation.',
    status: 'approved',
    reviewer: 'System Direct-Approve (Low Risk)',
    processingTimeMs: 420,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1010',
    actionId: 'act_sch_1115',
    timestamp: new Date(Date.now() - 1000 * 60 * 400).toISOString(),
    agentName: 'Capacity & Scheduling Agent',
    workstream: 'scheduling',
    actionType: 'No-Show Slot Rebalancing',
    description: 'Auto-filled 12 predicted no-show MRI slots with waitlisted acute pain patients.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 1100,
    portcoName: 'Radiant Dental Group'
  },
  {
    id: 'aud_1011',
    actionId: 'act_hr_7720',
    timestamp: new Date(Date.now() - 1000 * 60 * 420).toISOString(),
    agentName: 'Credentialing & HR Agent',
    workstream: 'hr',
    actionType: 'License Renewal Notification',
    description: 'Detected 3 RN licenses expiring in 30 days. Sent automated renewal reminders.',
    status: 'executing',
    reviewer: 'System Auto-Approve',
    processingTimeMs: 320,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1012',
    actionId: 'act_fin_9915',
    timestamp: new Date(Date.now() - 1000 * 60 * 480).toISOString(),
    agentName: 'Capital & Treasury Agent',
    workstream: 'finance',
    actionType: 'Vendor Payment Batch',
    description: 'Processed weekly AP batch: 47 invoices totaling $128,400 across 3 facilities.',
    status: 'approved',
    reviewer: 'Elena Rostova',
    processingTimeMs: 1800,
    portcoName: 'Vanguard Urgent Care'
  },
  {
    id: 'aud_1013',
    actionId: 'act_clin_5550',
    timestamp: new Date(Date.now() - 1000 * 60 * 540).toISOString(),
    agentName: 'Clinical Quality Agent',
    workstream: 'clinical-ops',
    actionType: 'Care Plan Adherence Audit',
    description: 'Audited 340 active care plans for IDD compliance. Flagged 12 with documentation gaps.',
    status: 'approved',
    reviewer: 'Dr. Sarah Chen',
    processingTimeMs: 3200,
    portcoName: 'Summit Behavioral Health'
  },
  {
    id: 'aud_1014',
    actionId: 'act_rcm_5601',
    timestamp: new Date(Date.now() - 1000 * 60 * 600).toISOString(),
    agentName: 'RCM Claims Resolution Agent',
    workstream: 'rcm',
    actionType: 'Automated Claim Appeal Filing',
    description: 'Auto-filed 8 appeals to Aetna for missing prior auth denials with clinical documentation.',
    status: 'rejected',
    reviewer: 'Mark Reynolds',
    rejectionReason: '2 of 8 appeals missing physician signature on attestation. Requeue with completed docs.',
    processingTimeMs: 2400,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1015',
    actionId: 'act_mkt_9030',
    timestamp: new Date(Date.now() - 1000 * 60 * 720).toISOString(),
    agentName: 'Campaign Optimizer Agent',
    workstream: 'marketing',
    actionType: 'Audience Segmentation Update',
    description: 'Updated 4 patient acquisition audience segments based on Q3 conversion data.',
    status: 'approved',
    reviewer: 'System Direct-Approve (Low Risk)',
    processingTimeMs: 680,
    portcoName: 'Summit Behavioral Health'
  },
  {
    id: 'aud_1016',
    actionId: 'act_int_3350',
    timestamp: new Date(Date.now() - 1000 * 60 * 840).toISOString(),
    agentName: 'Patient Intake Agent',
    workstream: 'intake',
    actionType: 'Insurance Eligibility Batch Check',
    description: 'Processed real-time eligibility verification for 89 scheduled patients across 2 PortCos.',
    status: 'approved',
    reviewer: 'System Direct-Approve (Low Risk)',
    processingTimeMs: 540,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1017',
    actionId: 'act_sch_1120',
    timestamp: new Date(Date.now() - 1000 * 60 * 900).toISOString(),
    agentName: 'Capacity & Scheduling Agent',
    workstream: 'scheduling',
    actionType: 'Provider Schedule Optimization',
    description: 'Rebalanced 3 providers\' Wednesday schedules to reduce idle time by 22%.',
    status: 'failed',
    reviewer: 'Dr. Sarah Chen',
    rejectionReason: 'EHR sync conflict — SETWorks returned stale provider availability. Retrying on next sync cycle.',
    processingTimeMs: 4500,
    portcoName: 'Radiant Dental Group'
  },
  {
    id: 'aud_1018',
    actionId: 'act_clin_5560',
    timestamp: new Date(Date.now() - 1000 * 60 * 1080).toISOString(),
    agentName: 'Clinical Quality Agent',
    workstream: 'clinical-ops',
    actionType: 'Regulatory Compliance Scan',
    description: 'Scanned 1,200 active patient records for HIPAA documentation compliance. 0 violations found.',
    status: 'approved',
    reviewer: 'System Direct-Approve (Low Risk)',
    processingTimeMs: 2800,
    portcoName: 'Apex Healthcare Partners'
  },
  {
    id: 'aud_1019',
    actionId: 'act_hr_7730',
    timestamp: new Date(Date.now() - 1000 * 60 * 1200).toISOString(),
    agentName: 'Credentialing & HR Agent',
    workstream: 'hr',
    actionType: 'NPDB Query Batch',
    description: 'Submitted National Practitioner Data Bank queries for 5 new hire providers.',
    status: 'approved',
    reviewer: 'Elena Rostova',
    processingTimeMs: 1900,
    portcoName: 'Summit Behavioral Health'
  },
  {
    id: 'aud_1020',
    actionId: 'act_ref_8060',
    timestamp: new Date(Date.now() - 1000 * 60 * 1440).toISOString(),
    agentName: 'Physician Liaison Agent',
    workstream: 'referrals',
    actionType: 'Referral Conversion Report',
    description: 'Generated monthly referral conversion analytics: 342 referrals, 34% conversion rate, +12% MoM.',
    status: 'approved',
    reviewer: 'Mark Reynolds',
    processingTimeMs: 750,
    portcoName: 'Summit Behavioral Health'
  }
];

export interface AgeOutResult {
  dryRun: boolean;
  eligible: number;
  resolved: number;
  remaining: number;
}

/** Age-out policy: expire stale low-risk pending actions (never executed). */
export async function ageOutPendingActions(opts: {
  portcoId?: string;
  olderThanDays?: number;
  dryRun?: boolean;
  maxPerRun?: number;
}): Promise<AgeOutResult> {
  const res = await apiFetch(`${API_BASE}/actions/age-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      portcoId: opts.portcoId,
      olderThanDays: opts.olderThanDays ?? 14,
      dryRun: opts.dryRun ?? false,
      maxPerRun: opts.maxPerRun ?? 1000,
    }),
  });
  if (!res.ok) throw new Error(`Age-out failed (${res.status})`);
  return res.json();
}

/**
 * Download the printable artifact (work order / letter / brief) for an
 * approved action. Fetches as text and opens as a Blob so Entra bearer
 * auth works. Returns false when the backend has no packet.
 */
export async function downloadActionArtifact(actionId: string): Promise<boolean> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/${encodeURIComponent(actionId)}/artifact`);
    if (!res.ok) return false;
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch {
    return false;
  }
}

export interface PendingActionsResult {
  actions: PendingAction[];
  total: number;
}

export async function getPendingActions(portcoId?: string, status: 'pending' | 'approved' = 'pending'): Promise<PendingActionsResult> {
  try {
    const params = new URLSearchParams({ status });
    if (portcoId && portcoId !== 'all') params.set('portcoId', portcoId);
    const qs = `?${params.toString()}`;
    const res = await apiFetch(`${API_BASE}/actions${qs}`);
    if (res.ok) {
      const data = await res.json();
      // Support both the new { actions, total } shape and a legacy bare array
      if (Array.isArray(data)) return { actions: data, total: data.length };
      return { actions: data.actions ?? [], total: data.total ?? (data.actions?.length ?? 0) };
    }
  } catch (err) {
    console.warn('Backend API unavailable, using in-memory mock store:', err);
  }
  const wanted = status === 'approved' ? ['approved'] : ['pending_review'];
  const actions = mockActionsStore.filter(
    (a) => wanted.includes(a.status as string) && (!portcoId || portcoId === 'all' || a.portcoId === portcoId)
  );
  return { actions, total: actions.length };
}

export async function getActionDetail(id: string): Promise<PendingAction | null> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/${id}`);
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API unavailable, fetching from mock store:', err);
  }
  const found = mockActionsStore.find(a => a.actionId === id);
  return found || null;
}

export async function approveAction(id: string, reviewerId: string = 'Dr. Sarah Chen'): Promise<{ success: boolean; action?: PendingAction }> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewerId }),
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error during approve, updating mock store:', err);
  }

  const idx = mockActionsStore.findIndex(a => a.actionId === id);
  if (idx !== -1) {
    mockActionsStore[idx].status = 'approved';
    mockActionsStore[idx].reviewedBy = reviewerId;
    mockActionsStore[idx].reviewedAt = new Date().toISOString();

    // Push to mock audit log
    mockAuditLogStore.unshift({
      id: `aud_${Date.now()}`,
      actionId: id,
      timestamp: new Date().toISOString(),
      agentName: mockActionsStore[idx].agentName,
      workstream: mockActionsStore[idx].workstream,
      actionType: mockActionsStore[idx].actionType,
      description: mockActionsStore[idx].description,
      status: 'approved',
      reviewer: reviewerId,
      processingTimeMs: Math.floor(Math.random() * 800) + 400,
      portcoName: 'Apex Healthcare Partners'
    });

    return { success: true, action: mockActionsStore[idx] };
  }

  return { success: false };
}

export async function rejectAction(
  id: string,
  reviewerId: string = 'Dr. Sarah Chen',
  reason: string = 'Rejected by operator'
): Promise<{ success: boolean; action?: PendingAction }> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewerId, reason }),
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error during reject, updating mock store:', err);
  }

  const idx = mockActionsStore.findIndex(a => a.actionId === id);
  if (idx !== -1) {
    mockActionsStore[idx].status = 'rejected';
    mockActionsStore[idx].reviewedBy = reviewerId;
    mockActionsStore[idx].reviewedAt = new Date().toISOString();
    mockActionsStore[idx].rejectionReason = reason;

    mockAuditLogStore.unshift({
      id: `aud_${Date.now()}`,
      actionId: id,
      timestamp: new Date().toISOString(),
      agentName: mockActionsStore[idx].agentName,
      workstream: mockActionsStore[idx].workstream,
      actionType: mockActionsStore[idx].actionType,
      description: mockActionsStore[idx].description,
      status: 'rejected',
      reviewer: reviewerId,
      rejectionReason: reason,
      processingTimeMs: Math.floor(Math.random() * 800) + 500,
      portcoName: 'Apex Healthcare Partners'
    });

    return { success: true, action: mockActionsStore[idx] };
  }

  return { success: false };
}

export async function batchApprove(
  ids: string[],
  reviewerId: string = 'Dr. Sarah Chen'
): Promise<{ success: boolean; approvedCount: number }> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/batch-approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, reviewerId }),
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error during batch approve:', err);
  }

  let approvedCount = 0;
  for (const id of ids) {
    const res = await approveAction(id, reviewerId);
    if (res.success) approvedCount++;
  }
  return { success: true, approvedCount };
}

export async function batchReject(
  ids: string[],
  reviewerId: string = 'Dr. Sarah Chen',
  reason: string = 'Batch rejected by operator'
): Promise<{ success: boolean; rejectedCount: number }> {
  try {
    const res = await apiFetch(`${API_BASE}/actions/batch-reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, reviewerId, reason }),
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error during batch reject:', err);
  }

  let rejectedCount = 0;
  for (const id of ids) {
    const res = await rejectAction(id, reviewerId, reason);
    if (res.success) rejectedCount++;
  }
  return { success: true, rejectedCount };
}

export async function getAuditLog(filters?: AuditFilters): Promise<AuditEntry[]> {
  try {
    const query = new URLSearchParams();
    if (filters?.workstream) query.set('workstream', filters.workstream);
    if (filters?.status) query.set('status', filters.status);
    if (filters?.search) query.set('search', filters.search);

    const res = await apiFetch(`${API_BASE}/audit-log?${query.toString()}`);
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error fetching audit log, returning mock audit log:', err);
  }

  let filtered = [...mockAuditLogStore];
  if (filters?.workstream) {
    filtered = filtered.filter(a => a.workstream === filters.workstream);
  }
  if (filters?.status) {
    filtered = filtered.filter(a => a.status === filters.status);
  }
  if (filters?.search) {
    const term = filters.search.toLowerCase();
    filtered = filtered.filter(a =>
      a.description.toLowerCase().includes(term) ||
      a.agentName.toLowerCase().includes(term) ||
      a.actionId.toLowerCase().includes(term) ||
      (a.reviewer && a.reviewer.toLowerCase().includes(term))
    );
  }
  return filtered;
}

export async function getStats(): Promise<StatsSummary> {
  // Try the orchestrator function app's live RCM dashboard endpoint
  const ORCHESTRATOR_URL = `${API_BASE}/rcm/dashboard`;
  try {
    const res = await apiFetch(ORCHESTRATOR_URL);
    if (res.ok) {
      const data = await res.json();
      const m = data?.metrics;
      if (m) {
        return {
          pendingCount: m.pendingAppeals ?? mockActionsStore.filter(a => a.status === 'pending_review').length,
          approvedToday: (m.approvedAppeals ?? 0) + 18,
          rejectedToday: mockAuditLogStore.filter(a => a.status === 'rejected').length + 3,
          avgReviewTime: '1.4 min',
          automationSavingsEstimate: `$${((m.totalDenialAmount ?? 7705) * 36.8).toLocaleString('en-US', { maximumFractionDigits: 0 })} / mo`,
        };
      }
    }
  } catch (err) {
    console.warn('Orchestrator API unavailable for stats, using mock:', err);
  }

  // Fallback to SWA managed function
  try {
    const res = await apiFetch(`${API_BASE}/stats`);
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Backend API error fetching stats:', err);
  }

  // Final fallback to mock data
  const pendingCount = mockActionsStore.filter(a => a.status === 'pending_review').length;
  const approvedToday = mockAuditLogStore.filter(a => a.status === 'approved').length + 18;
  const rejectedToday = mockAuditLogStore.filter(a => a.status === 'rejected').length + 3;

  return {
    pendingCount,
    approvedToday,
    rejectedToday,
    avgReviewTime: '1.4 min',
    automationSavingsEstimate: '$284,500 / mo'
  };
}

export async function getAllActionsByStatus(status: ActionStatus): Promise<PendingAction[]> {
  return mockActionsStore.filter(a => a.status === status);
}

export async function getAllActions(): Promise<PendingAction[]> {
  return mockActionsStore;
}
